/**
 * A tiny pixel buffer for icons: hard pixels drawn into a plain RGBA array, so icons are drawn and
 * tested without a canvas (Node has none) and only turned into a canvas once, when shown.
 */

/** A colour as `#rrggbb`. */
export type Hex = string;

const PACKED = new Map<string, number>();

/** A colour packed as a little-endian RGBA word (red in the low byte), as `ImageData` holds it. */
export function packed(hex: Hex, alpha = 255): number {
  const key = alpha === 255 ? hex : `${hex}/${alpha}`;
  let v = PACKED.get(key);
  if (v === undefined) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    v = ((alpha << 24) | (b << 16) | (g << 8) | r) >>> 0;
    PACKED.set(key, v);
  }
  return v;
}

export const rgbOf = (hex: Hex): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const h2 = (v: number) =>
  Math.max(0, Math.min(255, Math.round(v)))
    .toString(16)
    .padStart(2, '0');

/** `a` moved `k` of the way toward `b`. */
export function mix(a: Hex, b: Hex, k: number): Hex {
  const [r, g, bl] = rgbOf(a);
  const [r2, g2, b2] = rgbOf(b);
  return `#${h2(r + (r2 - r) * k)}${h2(g + (g2 - g) * k)}${h2(bl + (b2 - bl) * k)}`;
}

/** The shared colours of the icon set. */
export const C = {
  /** Warm ink black: the glass HUD's tint. */
  ink: '#16130f',
  /** Ink-wash paper: the windows' ground. */
  paper: '#f4f1ea',
  /** Seal red: the one accent, and the mark of a break (破) move and an ultimate (怒). */
  seal: '#b3261c',
  sealDark: '#4a0f0a',
  white: '#ffffff',
  steel: '#dfe7f2',
  steelMid: '#a9b6c8',
  steelDark: '#6a7890',
  wood: '#6a4630',
  woodDark: '#3a2a24',
  gold: '#f0c040',
  goldLight: '#ffe9a0',
  goldDark: '#a8721c',
  blood: '#d42a32',
  bloodLight: '#ff7a7a',
  bloodDark: '#7a0e16',
  poison: '#8ad84a',
  poisonDark: '#3e7a22',
  ice: '#bfeaff',
  iceDark: '#5aa8d8',
  jade: '#3fbf86',
  jadeDark: '#1f7a52',
  skin: '#f2c49a',
  skinDark: '#c88a5e',
  violet: '#c08aff',
  violetDark: '#6a3aa8',
  fire: '#ff8a2a',
  fireLight: '#ffd060',
  qi: '#9ff0c8',
} as const;

export class Pix {
  readonly d: Uint32Array;
  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.d = new Uint32Array(w * h);
  }

  inside(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }

  get(x: number, y: number): number {
    return this.inside(x, y) ? this.d[y * this.w + x]! : 0;
  }

  /** Whether a pixel is drawn (opaque). */
  solid(x: number, y: number): boolean {
    return this.get(x, y) >>> 24 > 0;
  }

  px(x: number, y: number, c: Hex): this {
    x = Math.round(x);
    y = Math.round(y);
    if (this.inside(x, y)) this.d[y * this.w + x] = packed(c);
    return this;
  }

  /** Clears a pixel back to transparent. */
  erase(x: number, y: number): this {
    if (this.inside(x, y)) this.d[y * this.w + x] = 0;
    return this;
  }

  rect(x: number, y: number, w: number, h: number, c: Hex): this {
    const v = packed(c);
    x = Math.round(x);
    y = Math.round(y);
    for (let j = Math.max(0, y); j < Math.min(this.h, y + h); j++)
      for (let i = Math.max(0, x); i < Math.min(this.w, x + w); i++) this.d[j * this.w + i] = v;
    return this;
  }

  /** Bresenham line, one pixel wide. */
  line(x0: number, y0: number, x1: number, y1: number, c: Hex): this {
    x0 = Math.round(x0);
    y0 = Math.round(y0);
    x1 = Math.round(x1);
    y1 = Math.round(y1);
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (let n = 0; n < 512; n++) {
      this.px(x0, y0, c);
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x0 += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y0 += sy;
      }
    }
    return this;
  }

  /** A line `w` pixels thick, widened across its run. */
  thick(x0: number, y0: number, x1: number, y1: number, c: Hex, w = 2): this {
    const steep = Math.abs(y1 - y0) > Math.abs(x1 - x0);
    for (let k = 0; k < w; k++) {
      const o = k - Math.floor((w - 1) / 2);
      if (steep) this.line(x0 + o, y0, x1 + o, y1, c);
      else this.line(x0, y0 + o, x1, y1 + o, c);
    }
    return this;
  }

  /** A polyline through the points. */
  path(pts: readonly (readonly [number, number])[], c: Hex, w = 1): this {
    for (let i = 0; i < pts.length - 1; i++) {
      const [a, b] = [pts[i]!, pts[i + 1]!];
      if (w === 1) this.line(a[0], a[1], b[0], b[1], c);
      else this.thick(a[0], a[1], b[0], b[1], c, w);
    }
    return this;
  }

  /** Filled ellipse built from horizontal spans, so its edge stays crisp. */
  ell(cx: number, cy: number, rx: number, ry: number, c: Hex): this {
    for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
      const dy = (y + 0.5 - cy) / ry;
      if (dy < -1 || dy > 1) continue;
      const hw = rx * Math.sqrt(1 - dy * dy);
      const x0 = Math.round(cx - hw);
      const x1 = Math.round(cx + hw);
      if (x1 > x0) this.rect(x0, y, x1 - x0, 1, c);
    }
    return this;
  }

  /** The rim of an ellipse from angle `a0` to `a1` (radians, 0 = right, clockwise on screen). */
  ring(cx: number, cy: number, rx: number, ry: number, c: Hex, a0 = 0, a1 = Math.PI * 2): this {
    const steps = Math.max(8, Math.ceil(Math.abs(a1 - a0) * Math.max(rx, ry) * 1.6));
    let last = '';
    for (let i = 0; i <= steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      const x = Math.round(cx + Math.cos(a) * rx);
      const y = Math.round(cy + Math.sin(a) * ry);
      const k = `${x},${y}`;
      if (k !== last) this.px(x, y, c);
      last = k;
    }
    return this;
  }

  /** Filled polygon (even-odd scanline fill through pixel centres). */
  poly(pts: readonly (readonly [number, number])[], c: Hex): this {
    const ys = pts.map((p) => p[1]);
    const y0 = Math.floor(Math.min(...ys));
    const y1 = Math.ceil(Math.max(...ys));
    for (let y = y0; y <= y1; y++) {
      const yc = y + 0.5;
      const xs: number[] = [];
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i]!;
        const [bx, by] = pts[(i + 1) % pts.length]!;
        if (ay <= yc !== by <= yc) xs.push(ax + ((yc - ay) / (by - ay)) * (bx - ax));
      }
      xs.sort((a, b) => a - b);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const xa = Math.round(xs[k]!);
        const xb = Math.round(xs[k + 1]!);
        if (xb > xa) this.rect(xa, y, xb - xa, 1, c);
      }
    }
    // The outline too, so thin slivers are not lost.
    this.path([...pts, pts[0]!], c);
    return this;
  }

  /** Draws another buffer over this one (transparent pixels let this one show). */
  blit(src: Pix, dx = 0, dy = 0): this {
    for (let y = 0; y < src.h; y++)
      for (let x = 0; x < src.w; x++) {
        const v = src.d[y * src.w + x]!;
        if (v >>> 24 && this.inside(x + dx, y + dy)) this.d[(y + dy) * this.w + x + dx] = v;
      }
    return this;
  }

  /** Every pixel of a colour turned into another. */
  recolor(from: Hex, to: Hex): this {
    const a = packed(from);
    const b = packed(to);
    for (let i = 0; i < this.d.length; i++) if (this.d[i] === a) this.d[i] = b;
    return this;
  }

  /** A one-pixel ring of `c` round every drawn pixel (four-way, so corners stay crisp). */
  outline(c: Hex): this {
    const v = packed(c);
    const src = this.d.slice();
    const { w, h } = this;
    const on = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && src[y * w + x]! >>> 24 > 0;
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++)
        if (!on(x, y) && (on(x - 1, y) || on(x + 1, y) || on(x, y - 1) || on(x, y + 1)))
          this.d[y * w + x] = v;
    return this;
  }

  /**
   * Flat cel light from the upper right: pixels on an edge facing it brighten a step, pixels on an
   * edge facing away darken a step. One-pixel strokes, which face both ways, keep their colour.
   */
  light(up = 0.3, down = 0.24): this {
    const src = this.d.slice();
    const { w, h } = this;
    const on = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && src[y * w + x]! >>> 24 > 0;
    const tint = (px: number, k: number) => {
      const f = (v: number, to: number) =>
        Math.max(0, Math.min(255, Math.round(k > 0 ? v + (to - v) * k : v * (1 + k))));
      return (
        ((px & 0xff000000) |
          (f((px >>> 16) & 255, 228) << 16) |
          (f((px >>> 8) & 255, 246) << 8) |
          f(px & 255, 255)) >>>
        0
      );
    };
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const px = src[y * w + x]!;
        if (!(px >>> 24)) continue;
        const lit = !on(x + 1, y) || !on(x, y - 1);
        const dark = !on(x - 1, y) || !on(x, y + 1);
        if (lit && !dark) this.d[y * w + x] = tint(px, up);
        else if (dark && !lit) this.d[y * w + x] = tint(px, -down);
      }
    return this;
  }

  /**
   * Half the size: each 2×2 block becomes the colour most of its drawn pixels share (a lone pixel
   * still counts, so thin strokes survive), brighter colours winning ties.
   */
  half(): Pix {
    const out = new Pix(this.w >> 1, this.h >> 1);
    const lum = (v: number) => (v & 255) * 3 + ((v >>> 8) & 255) * 6 + ((v >>> 16) & 255);
    for (let y = 0; y < out.h; y++)
      for (let x = 0; x < out.w; x++) {
        const vs = [
          this.get(2 * x, 2 * y),
          this.get(2 * x + 1, 2 * y),
          this.get(2 * x, 2 * y + 1),
          this.get(2 * x + 1, 2 * y + 1),
        ].filter((v) => v >>> 24);
        if (!vs.length) continue;
        let best = vs[0]!;
        let bestN = 0;
        for (const v of vs) {
          const n = vs.filter((u) => u === v).length;
          if (n > bestN || (n === bestN && lum(v) > lum(best))) {
            best = v;
            bestN = n;
          }
        }
        out.d[y * out.w + x] = best;
      }
    return out;
  }

  /** Twice the size, each pixel a 2×2 block. */
  double(): Pix {
    const out = new Pix(this.w * 2, this.h * 2);
    for (let y = 0; y < out.h; y++)
      for (let x = 0; x < out.w; x++) out.d[y * out.w + x] = this.d[(y >> 1) * this.w + (x >> 1)]!;
    return out;
  }

  /** Mirrored left to right. */
  flipX(): Pix {
    const out = new Pix(this.w, this.h);
    for (let y = 0; y < this.h; y++)
      for (let x = 0; x < this.w; x++) out.d[y * this.w + this.w - 1 - x] = this.d[y * this.w + x]!;
    return out;
  }

  /** Whether any pixel is drawn. */
  any(): boolean {
    return this.d.some((v) => v >>> 24 > 0);
  }
}
